import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, InputNumber, Select, Switch, Divider, Typography, Tag, Space, Row, Col, Tooltip, message, Empty } from 'antd';
import { InfoCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';

import {
  AutoModeSettings,
  AdPlatform,
  LifeSphere,
  AdCabinet,
  SemanticUnit,
  NODE_STATUS_CONFIG,
  NodeStatus,
} from '../types';
import * as ds from '../db/dataService';
import { useDb } from '../db/useDataService';

const { Text } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  productId: string;
  spheres: LifeSphere[];
  cabinets: AdCabinet[];
  productCabinetIds: string[];
  /** Колбек после сохранения. */
  onSaved?: (settings: AutoModeSettings) => void;
}

const PLATFORM_LABELS: Record<AdPlatform, string> = {
  yandex_direct: 'Яндекс.Директ',
  vk: 'ВКонтакте',
};

/** Собрать плоский список СЕ из иерархии с метаданными. */
const collectUnits = (spheres: LifeSphere[]): Array<SemanticUnit & { sphereName: string; themeName: string; subthemeName: string; nodeStatus: NodeStatus }> => {
  const out: Array<SemanticUnit & { sphereName: string; themeName: string; subthemeName: string; nodeStatus: NodeStatus }> = [];
  spheres.forEach((s) => {
    if (s.status === 'disabled') return;
    s.themes.forEach((t) => {
      if (t.status === 'disabled') return;
      t.subthemes.forEach((st) => {
        if (st.status === 'disabled') return;
        st.units.forEach((u) => {
          if (u.status === 'disabled') return;
          out.push({
            ...u,
            sphereName: s.name,
            themeName: t.name,
            subthemeName: st.name,
            nodeStatus: u.status || 'draft',
          });
        });
      });
    });
  });
  return out;
};

const DEFAULTS: Partial<AutoModeSettings> = {
  dailyBudget: 5000,
  maxBudgetPerCampaign: 5000,
  maxBudgetTotal: 150000,
  targetCpa: 1500,
  testDurationDays: 7,
  topNUnits: 10,
  platforms: ['yandex_direct'],
  cabinetIds: [],
  successCriteria: { minConversions: 3, maxCpa: 2000, minRomi: 50 },
  failCriteria: { zeroConversionsDays: 5, maxCpa: 3000, maxCpc: 50 },
  enabled: false,
};

const AutoModeSetupModal: React.FC<Props> = ({ visible, onClose, productId, spheres, cabinets, productCabinetIds, onSaved }) => {
  useDb();
  const [form] = Form.useForm();
  const existing = ds.getAutoModeSettings(productId);

  useEffect(() => {
    if (!visible) return;
    form.setFieldsValue({
      ...DEFAULTS,
      ...existing,
      cabinetIds: existing?.cabinetIds || productCabinetIds.slice(0, 1),
    });
  }, [visible, existing, form, productCabinetIds]);

  const [topN, setTopN] = useState<number>(existing?.topNUnits || DEFAULTS.topNUnits!);
  const [dailyBudget, setDailyBudget] = useState<number>(existing?.dailyBudget || DEFAULTS.dailyBudget!);
  const [maxPerCampaign, setMaxPerCampaign] = useState<number>(existing?.maxBudgetPerCampaign || DEFAULTS.maxBudgetPerCampaign!);

  const allUnits = useMemo(() => collectUnits(spheres), [spheres]);
  const topUnits = useMemo(() => {
    return allUnits
      .slice()
      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
      .slice(0, topN);
  }, [allUnits, topN]);

  const cabinetOptions = useMemo(
    () => cabinets
      .filter((c) => productCabinetIds.includes(c.id))
      .map((c) => ({ value: c.id, label: `${PLATFORM_LABELS[c.platform]}: ${c.name}` })),
    [cabinets, productCabinetIds],
  );

  // Прогноз: каждая запускаемая СЕ = 1 кампания. Бюджет на кампанию ограничен (maxPerCampaign / 7 в день).
  // dailyBudget делится между активными кампаниями.
  const forecast = useMemo(() => {
    const campaigns = Math.min(topN, allUnits.length);
    const perCampaignDaily = campaigns > 0 ? dailyBudget / campaigns : 0;
    const perCampaignWeekly = perCampaignDaily * 7;
    const cappedWeekly = Math.min(perCampaignWeekly, maxPerCampaign);
    const cappedDaily = cappedWeekly / 7;
    const totalDaily = cappedDaily * campaigns;
    return {
      campaigns,
      perCampaignDaily: Math.round(cappedDaily),
      perCampaignWeekly: Math.round(cappedWeekly),
      totalDaily: Math.round(totalDaily),
      totalWeekly: Math.round(totalDaily * 7),
      totalMonthly: Math.round(totalDaily * 30),
    };
  }, [topN, allUnits.length, dailyBudget, maxPerCampaign]);

  const handleSave = () => {
    form.validateFields().then((values) => {
      const settings: AutoModeSettings = {
        id: existing?.id || `am_${productId}`,
        productId,
        dailyBudget: values.dailyBudget,
        maxBudgetPerCampaign: values.maxBudgetPerCampaign,
        maxBudgetTotal: values.maxBudgetTotal,
        targetCpa: values.targetCpa,
        testDurationDays: values.testDurationDays,
        topNUnits: values.topNUnits,
        platforms: values.platforms,
        cabinetIds: values.cabinetIds || [],
        successCriteria: {
          minConversions: values.successMinConversions,
          maxCpa: values.successMaxCpa,
          minRomi: values.successMinRomi,
        },
        failCriteria: {
          zeroConversionsDays: values.failZeroConvDays,
          maxCpa: values.failMaxCpa,
          maxCpc: values.failMaxCpc,
        },
        enabled: values.enabled,
        createdAt: existing?.createdAt || new Date().toISOString(),
      };
      ds.saveAutoModeSettings(settings);
      message.success(values.enabled ? 'Авто-режим включён и сохранён' : 'Настройки сохранены');
      onSaved?.(settings);
      onClose();
    }).catch(() => message.error('Заполните обязательные поля'));
  };

  return (
    <Modal
      title={<Space><ThunderboltOutlined />Авто-режим: рамки для системы</Space>}
      visible={visible}
      onCancel={onClose}
      onOk={handleSave}
      okText="Сохранить"
      cancelText="Отмена"
      width={840}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark={false}
        onValuesChange={(_, all) => {
          if (typeof all.topNUnits === 'number') setTopN(all.topNUnits);
          if (typeof all.dailyBudget === 'number') setDailyBudget(all.dailyBudget);
          if (typeof all.maxBudgetPerCampaign === 'number') setMaxPerCampaign(all.maxBudgetPerCampaign);
        }}
      >
        <Divider orientation="left" plain>Бюджет</Divider>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="dailyBudget" label="Готов тратить в день, ₽" rules={[{ required: true }]}>
              <InputNumber min={100} step={500} style={{ width: '100%' }} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="maxBudgetPerCampaign" label={<>Макс. бюджет на кампанию (неделя), ₽ <Tooltip title="Жёсткий потолок недельного бюджета одной кампании. Защита от перерасхода."><InfoCircleOutlined /></Tooltip></>}>
              <InputNumber min={500} step={1000} style={{ width: '100%' }} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="maxBudgetTotal" label="Жёсткий потолок суммарно, ₽">
              <InputNumber min={1000} step={5000} style={{ width: '100%' }} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="targetCpa" label="Желаемая стоимость конверсии, ₽">
              <InputNumber min={1} step={100} style={{ width: '100%' }} formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="testDurationDays" label="Длительность теста, дней">
              <InputNumber min={1} max={30} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>Что запускаем</Divider>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="topNUnits" label="Сколько СЕ запустить (топ-N по приоритету)">
              <InputNumber min={1} max={200} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="platforms" label="Платформы" rules={[{ required: true, message: 'Выберите хотя бы одну' }]}>
              <Select mode="multiple" options={(Object.keys(PLATFORM_LABELS) as AdPlatform[]).map((k) => ({ value: k, label: PLATFORM_LABELS[k] }))} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="cabinetIds" label="Кабинеты">
              <Select mode="multiple" options={cabinetOptions} placeholder="Кабинеты для запуска" />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>Критерии успеха теста</Divider>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="successMinConversions" label="Минимум конверсий">
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="successMaxCpa" label="Макс. CPA, ₽">
              <InputNumber min={1} step={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="successMinRomi" label="Минимальный ROMI, %">
              <InputNumber min={-100} max={1000} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>Критерии провала теста</Divider>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="failZeroConvDays" label="Ноль конверсий N дней">
              <InputNumber min={1} max={30} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="failMaxCpa" label="Макс. CPA, ₽">
              <InputNumber min={1} step={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="failMaxCpc" label="Макс. CPC, ₽">
              <InputNumber min={1} step={5} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Divider />
        <Form.Item name="enabled" label="Включить авто-режим" valuePropName="checked">
          <Switch checkedChildren="Вкл" unCheckedChildren="Выкл" />
        </Form.Item>
      </Form>

      <Divider orientation="left" plain>Прогноз</Divider>
      <Space direction="vertical" size={6} style={{ width: '100%' }}>
        <Space wrap>
          <Tag color="blue">Кампаний: {forecast.campaigns}</Tag>
          <Tag color="cyan">На одну: {forecast.perCampaignDaily.toLocaleString('ru')} ₽/день · {forecast.perCampaignWeekly.toLocaleString('ru')} ₽/нед</Tag>
        </Space>
        <Space wrap>
          <Tag color="gold">Всего: {forecast.totalDaily.toLocaleString('ru')} ₽/день</Tag>
          <Tag>{forecast.totalWeekly.toLocaleString('ru')} ₽/нед</Tag>
          <Tag>{forecast.totalMonthly.toLocaleString('ru')} ₽/мес</Tag>
        </Space>
      </Space>

      <Divider orientation="left" plain>Топ-{topN} СЕ для запуска</Divider>
      {topUnits.length === 0 ? (
        <Empty description="Нет активных СЕ — задайте приоритет на карте смыслов" />
      ) : (
        <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
          {topUnits.map((u, i) => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: i < topUnits.length - 1 ? '1px solid #fafafa' : 'none' }}>
              <Tag color="purple" style={{ minWidth: 36, textAlign: 'center' }}>{i + 1}</Tag>
              <Tag>★ {u.priority ?? 0}</Tag>
              <Tag color={NODE_STATUS_CONFIG[u.nodeStatus].color} style={{ fontSize: 10 }}>{NODE_STATUS_CONFIG[u.nodeStatus].label}</Tag>
              <Text style={{ flex: 1 }} ellipsis>{u.name}</Text>
              <Text type="secondary" style={{ fontSize: 11 }} ellipsis>{u.sphereName} / {u.themeName} / {u.subthemeName}</Text>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default AutoModeSetupModal;
